// ProcessTimeline.js - Portugal
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Golden Visa',
      stages: [
        { step: 'Set up the investment and paperwork', duration: '1-3 months', description: 'Secure the investment, obtain your NIF and bank account, and gather the documents' },
        { step: 'Lodge the application', duration: '1-2 weeks', description: 'File the application together with every supporting document' },
        { step: 'Biometrics', duration: '2-4 weeks', description: 'Attend the biometrics appointment, either in Portugal or at a consulate' },
        { step: 'Assessment', duration: '6-12 months', description: 'SEF works through the application and verifies the investment' },
        { step: 'Permit issued', duration: '1-2 weeks', description: 'A 2-year residence permit card is issued to you' },
        { step: 'Renewal at the 2-year mark', duration: '2-4 months', description: 'Renew for a further 3 years, with permanent residency available at 5' }
      ],
      totalTime: '8-18 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'D7 Visa (Passive Income)',
      stages: [
        { step: 'Assemble the income evidence', duration: '2-4 weeks', description: 'Pull together proof of passive income, bank statements and accommodation' },
        { step: 'Apply at the consulate', duration: '1-2 weeks', description: 'Submit the D7 application at the Portuguese consulate in your home country' },
        { step: 'Assessment', duration: '2-4 months', description: 'The consulate reviews the application and the documents behind it' },
        { step: 'Visa granted, travel arranged', duration: '1-2 weeks', description: 'Once the D7 is issued you have 4 months in which to travel to Portugal' },
        { step: 'SEF appointment', duration: '1-3 months', description: 'Book and attend the SEF appointment in Portugal for your residence card' },
        { step: 'Card issued', duration: '2-4 weeks', description: 'A 2-year residence permit card is issued' }
      ],
      totalTime: '6-12 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Permanent Residency (after 5 years)',
      stages: [
        { step: 'Confirm you qualify', duration: '1-2 weeks', description: 'Check that 5 years of legal residency and every condition have been met' },
        { step: 'Collect the paperwork', duration: '2-4 weeks', description: 'Refresh your documents, tax records and criminal record certificate' },
        { step: 'File the application', duration: '1 week', description: 'Send the permanent residency application in to SEF' },
        { step: 'Assessment', duration: '6-12 months', description: 'SEF works through the permanent residency application' },
        { step: 'Permanent residence granted', duration: '1-2 weeks', description: 'You receive a permanent residence permit, which carries no expiry' }
      ],
      totalTime: '8-14 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What the Timeline Looks Like
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Every route runs at its own pace. Knowing which stage takes longest is what lets you plan around it.
          </p>
        </motion.div>

        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Start to finish, typically: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>
                      <div className="bg-surface p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-primary">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-secondary bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              What Affects the Wait
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Whether the documentation is accurate and nothing is missing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>How long verification of the investment and due diligence take</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>SEF capacity, and how quickly appointments free up</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>The background and verification checks themselves</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              What We Handle
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Talking through the investment options and getting them in place</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Making sure the documentation is accurate and complete</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Working alongside your lawyers and investment advisers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Tracking the application and answering whatever SEF asks for</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
